import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {DividerWithMargin} from 'components/OfferList/CompactOfferList/CompactOfferList'
import {OfferTrackingContextProvider} from 'context/offerTracking'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getIsRtlLanguage} from 'modules/meta/selectors'
import {getAllOffersOverlay} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {
  getOfferEntity,
  getSearchParameters,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {getAllOffersWithSplitBookings} from 'modules/sapiSearch/utils'

import {NavBar, NavBarTitle} from '@vio/atlas/NavBar'
import {ANIMATION_TYPES, Overlay} from '@vio/atlas/Overlay'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon/Icon'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {HotelOfferCard} from '../../HotelOfferCard'

const DESKTOP_OVERLAY_WIDTH = 520
const SOURCE_COMPONENT = 'DesktopAllOffersOverlay'

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`

export const AllOffersDesktopOverlay = () => {
  const dispatch = useDispatch()
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const hotelId = useSelector(getAccommodationPageHotelId)
  const overlay = useSelector(getAllOffersOverlay)
  const isOpen = Boolean(overlay)

  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const offers = getAllOffersWithSplitBookings(offerEntity, splitBookingDetails)

  const searchedParameters = useSelector(getSearchParameters)

  const onClose = () => {
    dispatch(hideOverlay())
  }

  return (
    <Overlay
      animatePresenceInitial
      enableBodyScrollLock
      transitionDuration={300}
      isOpen={isOpen}
      onShadeBackgroundClick={onClose}
      containerStyles={{
        width: DESKTOP_OVERLAY_WIDTH,
        marginLeft: `calc(100vw - ${DESKTOP_OVERLAY_WIDTH}px)`
      }}
      animationType={
        isRtlLanguage ? ANIMATION_TYPES.SLIDE_LEFT : ANIMATION_TYPES.SLIDE_RIGHT
      }
      onClose={onClose}
      closeOnEsc={true}
    >
      <StickyHeader>
        <NavBar
          isCompact
          centerAligned
          withDivider
          centerContent={
            <NavBarTitle>
              <FormattedMessageWrapper
                defaultMessage="All deals ({amount})"
                id="allOffers"
                values={{amount: offers.length}}
              />
            </NavBarTitle>
          }
          endContent={
            <Button onClick={onClose} variant="ghost">
              <Icon name="Close" />
            </Button>
          }
        />
      </StickyHeader>
      <OfferTrackingContextProvider
        offerEntity={offerEntity}
        parameters={searchedParameters}
      >
        {isOpen && (
          <>
            <Box paddingRight="s600" paddingLeft="s600">
              {offers.map((offer, index) => (
                <React.Fragment key={offer.id}>
                  <Box paddingTop="s600" paddingBottom="s600">
                    <HotelOfferCard
                      offer={offer}
                      offerPosition={index}
                      sourceComponent={SOURCE_COMPONENT}
                      variant="horizontal"
                    />
                  </Box>
                  {index !== offers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>

            <DividerWithMargin />
            <Box
              display="flex"
              padding="s300"
              paddingBottom="s600"
              justifyContent="center"
              alignItems="center"
              backgroundColor="background.neutral.primary"
            >
              <Text variant="bodyXS" colorPath="content.neutral.secondary">
                <FormattedMessageWrapper
                  id="noMoreDeals"
                  defaultMessage="No more deals to show"
                />
              </Text>
            </Box>
          </>
        )}
      </OfferTrackingContextProvider>
    </Overlay>
  )
}
